// Payroll Engine Factory - creates appropriate engine instances based on country

import { CountryCode } from './types';
import { PayrollEngine } from './PayrollEngine';

/**
 * Factory class for creating country-specific payroll engines
 * Implements a registry pattern for easy extension
 */
export class PayrollEngineFactory {
  private static engineRegistry: Map<CountryCode, () => PayrollEngine> = new Map();
  private static defaultEngine: (() => PayrollEngine) | null = null;

  /**
   * Register a payroll engine for a specific country
   */
  static registerEngine(countryCode: CountryCode, engineFactory: () => PayrollEngine): void {
    this.engineRegistry.set(countryCode.toUpperCase(), engineFactory);
  }

  /**
   * Register multiple engines at once
   */
  static registerEngines(engines: Record<CountryCode, () => PayrollEngine>): void {
    Object.entries(engines).forEach(([countryCode, engineFactory]) => {
      this.registerEngine(countryCode, engineFactory);
    });
  }

  /**
   * Set a default engine for unsupported countries
   */
  static setDefaultEngine(engineFactory: () => PayrollEngine): void {
    this.defaultEngine = engineFactory;
  }

  /**
   * Create a payroll engine for the specified country
   */
  static createEngine(countryCode: CountryCode, debugMode: boolean = false): PayrollEngine {
    const normalizedCountryCode = countryCode.toUpperCase();
    const engineFactory = this.engineRegistry.get(normalizedCountryCode);
    
    if (engineFactory) {
      const engine = engineFactory();
      // Set debug mode if the engine supports it
      if ('debugMode' in engine && typeof engine.debugMode !== 'undefined') {
        (engine as any).debugMode = debugMode;
      }
      return engine;
    }

    // Try default engine if available
    if (this.defaultEngine) {
      console.warn(`No specific payroll engine found for ${countryCode}, using default engine`);
      return this.defaultEngine();
    }

    throw new Error(`No payroll engine registered for country: ${countryCode}. Supported countries: ${Array.from(this.engineRegistry.keys()).join(', ')}`);
  }

  /**
   * Check if a country is supported
   */
  static isCountrySupported(countryCode: CountryCode): boolean {
    return this.engineRegistry.has(countryCode.toUpperCase()) || this.defaultEngine !== null;
  }

  /**
   * Get list of supported countries
   */
  static getSupportedCountries(): CountryCode[] {
    return Array.from(this.engineRegistry.keys());
  }

  /**
   * Get engine information for debugging
   */
  static getEngineInfo(): Record<string, any> {
    return {
      supportedCountries: this.getSupportedCountries(),
      hasDefaultEngine: this.defaultEngine !== null,
      totalEngines: this.engineRegistry.size
    };
  }

  /**
   * Clear all registered engines (useful for testing)
   */
  static clearRegistry(): void {
    this.engineRegistry.clear();
    this.defaultEngine = null;
  }
}

/**
 * Convenience function to create an engine
 */
export function createPayrollEngine(countryCode: CountryCode, debugMode: boolean = false): PayrollEngine {
  return PayrollEngineFactory.createEngine(countryCode, debugMode);
}

/**
 * Register a single engine - utility function
 */
export function registerPayrollEngine(countryCode: CountryCode, engineClass: new (...args: any[]) => PayrollEngine) {
  PayrollEngineFactory.registerEngine(countryCode, () => new engineClass(countryCode));
}

/**
 * Utility to register all engines from a module
 */
export function registerAllEngines(engineModule: Record<string, any>): void {
  Object.entries(engineModule).forEach(([key, value]) => {
    // Look for classes that extend PayrollEngine
    if (typeof value === 'function' && value.prototype instanceof PayrollEngine) {
      // Extract country code from class name or use a convention
      const countryCode = extractCountryCodeFromClassName(key);
      if (countryCode) {
        PayrollEngineFactory.registerEngine(countryCode, () => new value(countryCode));
      }
    }
  });
}

/**
 * Helper function to extract country code from class name
 * Examples: SlovakPayrollEngine -> SK, GermanPayrollEngine -> DE
 */
function extractCountryCodeFromClassName(className: string): CountryCode | null {
  const countryMap: Record<string, CountryCode> = {
    'slovak': 'SK',
    'german': 'DE', 
    'austrian': 'AT',
    'french': 'FR',
    'spanish': 'ES',
    'italian': 'IT',
    'polish': 'PL',
    'czech': 'CZ',
    'hungarian': 'HU',
    'dutch': 'NL',
    'belgian': 'BE',
    'portuguese': 'PT',
    'swedish': 'SE',
    'danish': 'DK',
    'finnish': 'FI',
    'norwegian': 'NO',
    'american': 'US',
    'british': 'GB',
    'canadian': 'CA',
    'australian': 'AU'
  };

  const lowerClassName = className.toLowerCase();
  for (const [countryName, countryCode] of Object.entries(countryMap)) {
    if (lowerClassName.includes(countryName)) {
      return countryCode;
    }
  }
  
  return null;
}